<template>
  <c-trigger
    v-model:popup-visible="visible"
    :click-to-close="false"
    :disabled="props.readonly || props.disabled"
    auto-fit-popup-min-width
  >
    <a-input
      class="pati_name_input"
      v-model="nameValue"
      :placeholder="props.placeholder"
      :allow-clear="props.allowClear && !props.readonly && !props.disabled"
      :readonly="props.readonly"
      :disabled="props.disabled"
      @input="handleInput"
      @clear="handleClear"
    ></a-input>
    <template #content>
      <div
        ref="listBoxDom"
        class="patient_list"
        v-loading="loading && patientList.length === 0"
      >
        <template v-if="patientList.length > 0">
          <el-scrollbar @scroll="handleReachBottom">
            <div ref="scrollbarDom">
              <div
                v-for="item in patientList"
                :key="item.id"
                @click="handleChoose(item)"
              >
                <slot name="option" :option="item">
                  <div class="patient_option">
                    <p class="name">{{ item.patiname }}</p>
                    <a-space :size="12">
                      <span v-if="item.sex">{{ item.sex }}</span>
                      <span v-if="item.birthdatetime">
                        {{ $filters.formatDate(item.birthdatetime) }}
                      </span>
                      <span v-if="item.tel">{{ item.tel }}</span>
                    </a-space>
                  </div>
                </slot>
              </div>
              <a-spin v-if="loading" :size="16"></a-spin>
            </div>
          </el-scrollbar>
        </template>
        <a-empty v-else></a-empty>
      </div>
    </template>
  </c-trigger>
</template>

<script setup>
import _ from 'lodash';
import { nextTick, onBeforeMount, ref, watch } from 'vue';
import { getPatientList, getPatientInfo } from '@/api/patient.js';
import { useFormItem } from '@arco-design/web-vue';

const { eventHandlers } = useFormItem();
const emit = defineEmits(['input', 'change', 'clear', 'update:modelValue']);
const props = defineProps({
  modelValue: {
    type: String,
  },
  placeholder: {
    type: String,
  },
  allowClear: {
    type: Boolean,
    default: false,
  },
  allowRestore: {
    type: Boolean,
    default: false,
  },
  readonly: Boolean,
  disabled: Boolean,
});
const nameValue = ref(props.modelValue || '');
const visible = ref(false);
const loading = ref(false);
const page = ref(0);
const hasNextPages = ref(true);
const patientList = ref([]);
watch(
  () => nameValue.value,
  (value) => {
    emit('update:modelValue', value);
  },
  { immediate: true, deep: true },
);
const initData = async (type) => {
  if (!hasNextPages.value) return false;
  loading.value = true;
  page.value++;
  const res = await getPatientList({
    page: page.value,
    limit: 10,
    queryText: nameValue.value,
  }).catch(() => {
    loading.value = false;
  });
  loading.value = false;
  hasNextPages.value = res.data.hasNextPages;
  if (type === 'reset') {
    patientList.value = res.data.items;
  } else {
    patientList.value.push(...res.data.items);
  }
};
const search = async () => {
  page.value = 0;
  hasNextPages.value = true;
  await initData('reset');
};
const handleInput = (val) => {
  nameValue.value = val;
  emit('input', val);
  eventHandlers.value?.onChange?.(val);
};
const handleClear = () => {
  handleSearch();
  eventHandlers.value?.onChange?.(null);
  emit('clear');
};
const handleSearch = _.debounce(search, 200);
watch(
  () => props.modelValue,
  (val) => {
    nameValue.value = val;
    handleSearch(val || '');
  },
  { immediate: true },
);
const handleChoose = async (item) => {
  nameValue.value = item.patiname;
  eventHandlers.value?.onChange?.(item);
  const res = await getPatientInfo(item.id);
  visible.value = false;
  const emitChangeData = { ...res.data, starTel: item.tel };
  emit('change', emitChangeData);
  eventHandlers.value?.onChange?.(emitChangeData);
};
const listBoxDom = ref(null);
const scrollbarDom = ref(null);
const handleReachBottom = _.throttle(({ scrollTop }) => {
  let clientHeight = listBoxDom.value.clientHeight;
  let scrollHeight = scrollbarDom.value.clientHeight;
  nextTick(() => {
    let distance = scrollHeight - scrollTop - clientHeight;
    if (distance <= 50) {
      initData();
    }
  });
}, 500);
</script>

<style lang="scss" scoped>
@import '@/assets/css/mixin.scss';
.patient_list {
  @include boxBordered;
  &,
  ::v-deep(.el-scrollbar),
  ::v-deep(.el-scrollbar__wrap),
  ::v-deep(.el-scrollbar__view) {
    max-height: 350px !important;
  }
  .patient_option {
    @include FontSize_x12;
    color: var(--color_black);
    cursor: pointer;
    padding: 6px 12px;
    &:hover {
      background-color: var(--color_hover);
    }
    .name {
      @include FontSize_x14;
      margin-bottom: 4px;
    }
  }
  .arco-spin {
    @include flex(center);
    width: 100%;
    padding: 4px 0;
  }
}
</style>
